import { refreshTokensIfNeeded } from "@/api/service"
import { isTokenExpired, getTokenFromStorage } from "@/helpers/jwt"
import { createSocketBase } from "@/store/apis/socket.factory"
import {
  IMovementFinishedData,
  IMovementStartedData,
  IMovementStepData,
  IMovementStoppedData,
  IWorldMapEvent,
  SOCKET_EVENTS
} from "@/store/apis/socketRegistry"
import { fromSocket } from "@/store/apis/worldMapAPI/movementAdapters"
import { applyPlanWrite } from "@/store/apis/worldMapAPI/movementMerge"
import { IMovementPlan } from "@/store/apis/worldMapAPI/worldMap.types"
import {
  setConnectionStatus,
  setMovementTerminal,
  setRealtimeError
} from "@/store/slices/realtime"

type IPlanUpdater = (
  recipe: (draft: IMovementPlan | null) => IMovementPlan | null
) => void

export const worldMapSocket = {
  ...createSocketBase("world-map"),

  updateData: null as IPlanUpdater | null,
  refetch: null as (() => void) | null,
  reinitGuard: false,

  setUpdateData(fn: IPlanUpdater | null) {
    this.updateData = fn
  },
  setRefetch(fn: (() => void) | null) {
    this.refetch = fn
  },

  start() {
    this._init({
      auth: () => ({ token: getTokenFromStorage("accessToken") ?? "" }),
      onConnect: () => {
        this.dispatch?.(setConnectionStatus("connected"))
        this.dispatch?.(setRealtimeError(null))
        this.reinitGuard = false
      },
      onReconnectAttempt: () => {
        this.dispatch?.(setConnectionStatus("reconnecting"))
      },
      onReconnect: () => {
        this.refetch?.()
      },
      onDisconnect: () => {
        this.dispatch?.(setConnectionStatus("disconnected"))
      },
      onConnectError: (error: Error) => {
        const token = getTokenFromStorage("accessToken")
        if (token && isTokenExpired(token) && !this.reinitGuard) {
          this.reinitGuard = true
          void refreshTokensIfNeeded().then((ok) => {
            if (ok) {
              this._reset()
              this.start()
            } else {
              this.dispatch?.(setRealtimeError(error.message))
              this.dispatch?.(setConnectionStatus("disconnected"))
            }
          })
        } else {
          this.dispatch?.(setRealtimeError(error.message))
        }
      }
    })
  },

  bindMovementEvents() {
    const write = (plan: IMovementPlan, kind: "replace" | "step"): void => {
      this.updateData?.((draft) => applyPlanWrite(draft, plan, kind))
    }
    const unsubs = [
      this._bind(
        SOCKET_EVENTS.movementStarted,
        (e: IWorldMapEvent<IMovementStartedData>) =>
          write(fromSocket(e), "replace")
      ),
      this._bind(
        SOCKET_EVENTS.movementRerouted,
        (e: IWorldMapEvent<IMovementStartedData>) =>
          write(fromSocket(e), "replace")
      ),
      this._bind(
        SOCKET_EVENTS.movementStep,
        (e: IWorldMapEvent<IMovementStepData>) =>
          this.updateData?.((draft) => reanchorStep(draft, e))
      ),
      this._bind(
        SOCKET_EVENTS.movementFinished,
        (e: IWorldMapEvent<IMovementFinishedData>) =>
          this.dispatch?.(
            setMovementTerminal({ kind: "ARRIVED", position: e.data.position })
          )
      ),
      this._bind(
        SOCKET_EVENTS.movementStopped,
        (e: IWorldMapEvent<IMovementStoppedData>) =>
          this.dispatch?.(
            setMovementTerminal({
              kind: e.data.reason as "CANCELLED" | "INTERRUPTED",
              position: e.data.position
            })
          )
      )
    ]
    return () => unsubs.forEach((u) => u())
  }
}

const reanchorStep = (
  draft: IMovementPlan | null,
  e: IWorldMapEvent<IMovementStepData>
): IMovementPlan | null => {
  if (!draft) return draft
  const occurredAt = Date.parse(e.occurredAt)
  if (occurredAt <= draft.lastAppliedOccurredAt) return draft
  const reachedCell = draft.route[0]
  if (
    !reachedCell ||
    reachedCell.x !== e.data.position.x ||
    reachedCell.y !== e.data.position.y
  ) {
    return draft
  }
  const remaining = draft.route.slice(1)
  if (remaining.length === 0) return null
  return {
    ...draft,
    from: e.data.position,
    to: e.data.step.to,
    startedAtMs: Date.parse(e.data.step.startedAt),
    endsAtMs: Date.parse(e.data.step.endsAt),
    route: remaining,
    lastAppliedOccurredAt: occurredAt
  }
}
